import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Flex, Button, Center, Icon } from '@chakra-ui/react';
import { KDrawer } from '@/ui-library/components/molecules/KDrawer/KDrawer';
import { RequisitionHeader } from './RequisitionHeader';
import { useDateFormat, useShowToast, useQueryState } from '@/hooks';
import type { RequisitionsDetailsDrawerProps } from '../types';
import { API_KEYS, API_SERVICE } from '../api';
import { useRequisitionGroupData } from '../hooks';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CenterSpinner, ModalWithForm } from '@/components/ats';
import { RequisitionDetailsBody } from './RequisitionDetailsBody';
import useUrlState from '@ahooksjs/use-url-state';
import { useLazyGetApprovalsByEntityQuery } from '@/store/query/approval-builder';
import { useApproveRejectActionMutation } from '@/store/query/approval-builder';
import { useSelector } from 'react-redux';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { KText } from '@/ui-library/components';
import { ModalCloseButton } from '@/ui/ModalClosebutton';
import { Link, useLocation } from 'react-router-dom';
import { SliderButtons } from '@/components/candidate-job-slider-v1/components/SliderButtons';
import { XIcon } from '@heroicons/react/solid';
import { useIsMobile } from '@/hooks/use-IsMobile';
import { MOBILE_BREAKPOINT } from '@/constants/mobile-breakpoints';
import { PartOfGroupTag } from './PartOfGroupTag';
// Utility function to parse action from URL
const parseActionFromUrl = (
  urlState: any,
  locationSearch: string
): string | null => {
  if (urlState?.action) {
    return urlState.action;
  }

  const search = locationSearch || '';
  const match = search.match(/[?&]action=([^&]*)/);
  if (match) {
    return decodeURIComponent(match[1]);
  }

  const requisitionIdMatch = search.match(/[?&]requisitionId=([^&]*)/);
  if (requisitionIdMatch) {
    const decodedRequisitionId = decodeURIComponent(requisitionIdMatch[1]);
    const actionMatch = decodedRequisitionId.match(/[?&]action=([^&]*)/);
    if (actionMatch) {
      return decodeURIComponent(actionMatch[1]);
    }
  }

  return null;
};

// Reject form configuration
const REJECT_FORM_FIELDS = [
  {
    name: 'note',
    type: 'textArea',
    label: 'Notes',
    placeholder: 'Write your reason here',
  },
];

// Editable requisition statuses
const EDITABLE_STATUSES = [
  'open',
  'approved',
  'pending_approval',
  'rejected',
  'closed',
  'created',
  'filled',
];

export const RequisitionsDetailsDrawer: React.FC<RequisitionsDetailsDrawerProps> =
  ({
    isOpen,
    onClose,
    requisitionId,
    readOnlyMode = false,
    onActionComplete,
    onNext,
    onPrev,
    requisitions = [],
    isGrouped = false,
  }) => {
    const { dateFormat } = useDateFormat();
    const [urlState, setUrlState] = useUrlState();
    const { queryParams } = useQueryState();
    const location = useLocation();
    const queryClient = useQueryClient();
    const toast = useShowToast();
    const currUser = useSelector(selectCurrUser);

    const isMobile = useIsMobile({ mobileBreakpoint: MOBILE_BREAKPOINT });

    // Check if opened via requisitionGroupId URL param
    const requisitionGroupId = queryParams?.['requisitionGroupId'];
    const shouldApplyToGroupFromUrl = !!requisitionGroupId;

    const [showRejectModal, setShowRejectModal] = useState(false);
    const [processedActionId, setProcessedActionId] = useState<string | null>(
      null
    );
    const [autoTriggeredAction, setAutoTriggeredAction] = useState(false);
    const [shouldOpenHeaderMenu, setShouldOpenHeaderMenu] = useState(false);

    const [getApprovalData, { data: approvalData }] =
      useLazyGetApprovalsByEntityQuery();

    const [approverAcceptAction, { isLoading: isApproveActionLoading }] =
      useApproveRejectActionMutation();

    const {
      data: requisitionData,
      isLoading,
      isFetching,
      refetch: refetchRequisition,
    } = useQuery({
      queryKey: [API_KEYS.getRequisition, requisitionId],
      queryFn: () => API_SERVICE.getRequisition(requisitionId),
      staleTime: 0,
    });

    const {
      data: requisitionsFormFieldsData,
      isLoading: isRequisitionsFormFieldsLoading,
      isFetching: isRequisitionsFormFieldsFetching,
    } = useQuery({
      queryKey: [API_KEYS.getRequisitionsFormFields, requisitionId],
      queryFn: () => API_SERVICE.getRequisitionsFormFields(),
      staleTime: 0,
    });

    const requisition = requisitionData?.requisition;

    // Get group-related data
    const { isPartOfGroup, availableRequisitions, allGroupRequisitions } =
      useRequisitionGroupData({
        requisition,
        isOpen,
      });

    const isRequisitionLoading =
      isLoading ||
      isFetching ||
      isRequisitionsFormFieldsLoading ||
      isRequisitionsFormFieldsFetching;

    // Parse action from URL
    const urlAction = useMemo(
      () => parseActionFromUrl(urlState, location.search),
      [urlState, location.search]
    );

    // Check if current user is approver
    const isCurrentUserApprover = useMemo(() => {
      if (requisition?.status !== 'pending_approval') return false;

      const currentActiveStep = approvalData?.approval_steps?.find(
        (step) => step?.status === 'in_progress'
      );

      const currentStepUsers =
        currentActiveStep?.approval_user_groups?.[0]?.approval_users
          ?.filter((user: any) => user?.approval_log === null)
          ?.map((userObj: any) => userObj?.user?.id);

      return currentStepUsers?.includes(currUser?.id) || false;
    }, [approvalData, requisition?.status, currUser?.id]);

    const isReqAssociated = requisition?.requisition_association?.id;
    const canEdit =
      requisition && EDITABLE_STATUSES.includes(requisition.status);

    // Helper function to invalidate queries
    const invalidateRequisitionQueries = useCallback(
      async (openHeaderMenu = false) => {
        queryClient.invalidateQueries({
          queryKey: ['getApprovalsByEntity'],
        });

        // Invalidate requisitions list query to update list view
        queryClient.invalidateQueries({
          queryKey: [API_KEYS.getRequisitions],
        });

        if (openHeaderMenu) {
          const { data: refetchedData } = await refetchRequisition();
          const updatedRequisition = refetchedData?.requisition;
          if (updatedRequisition?.status === 'pending_approval') {
            setShouldOpenHeaderMenu(true);
          }
        } else {
          queryClient.invalidateQueries({
            queryKey: [API_KEYS.getRequisition, requisitionId.toString()],
          });
        }

        if (requisition?.id) {
          getApprovalData({ id: requisition.id, type: 'requisition' });
        }
      },
      [
        queryClient,
        requisitionId,
        requisition?.id,
        getApprovalData,
        refetchRequisition,
      ]
    );

    // Callback for approval changes from status tag
    const handleApprovalChange = useCallback(() => {
      invalidateRequisitionQueries();
    }, [invalidateRequisitionQueries]);

    // Handle approve action (used for both manual and auto-triggered)
    const handleApproveAction = useCallback(
      async (isAutoTriggered = false) => {
        if (!requisition?.id) return;

        const payload: any = {
          type: 'requisition',
          entity_id: requisition.id,
          log_action: 'approve',
          note: '',
        };

        // Add apply_to_group if opened via requisitionGroupId or if part of group
        const shouldApplyToGroup = shouldApplyToGroupFromUrl || isPartOfGroup;
        if (shouldApplyToGroup) {
          payload.apply_to_group = true;
        }

        const response = await approverAcceptAction({
          payload,
        });

        if ('error' in response) {
          toast({
            title: 'Unable to approve the requisition',
            description: response?.error?.[0],
            status: 'error',
          });
          return;
        }

        toast({
          title: shouldApplyToGroup
            ? 'Requisition group approved successfully'
            : 'Requisition approved successfully',
        });

        invalidateRequisitionQueries();

        if (!isAutoTriggered) {
          onActionComplete?.();
        } else {
          setShouldOpenHeaderMenu(true);
        }
      },
      [
        requisition?.id,
        approverAcceptAction,
        toast,
        invalidateRequisitionQueries,
        onActionComplete,
        shouldApplyToGroupFromUrl,
        isPartOfGroup,
      ]
    );

    // Handle reject action
    const handleRejectAction = useCallback(
      async (note: string = '--', isAutoTriggered = false) => {
        if (!requisition?.id) return;

        const payload: any = {
          type: 'requisition',
          entity_id: requisition.id,
          log_action: 'reject',
          note,
        };

        // Add apply_to_group if opened via requisitionGroupId or if part of group
        const shouldApplyToGroup = shouldApplyToGroupFromUrl || isPartOfGroup;
        if (shouldApplyToGroup) {
          payload.apply_to_group = true;
        }

        const response = await approverAcceptAction({
          payload,
        });

        if ('error' in response) {
          toast({
            title: 'Unable to reject the requisition',
            description: response?.error?.[0],
            status: 'error',
          });
          return;
        }

        toast({
          title: shouldApplyToGroup
            ? 'Requisition group has been rejected'
            : 'Requisition has been rejected',
        });

        setShowRejectModal(false);

        if (isAutoTriggered) {
          setUrlState({ action: undefined });
          await invalidateRequisitionQueries(true);
          setAutoTriggeredAction(true);
        } else {
          onActionComplete?.();
        }

        invalidateRequisitionQueries();
      },
      [
        requisition,
        approverAcceptAction,
        toast,
        setUrlState,
        onActionComplete,
        invalidateRequisitionQueries,
        shouldApplyToGroupFromUrl,
        isPartOfGroup,
      ]
    );

    // Reset state when requisitionId changes
    useEffect(() => {
      setProcessedActionId(null);
      setShouldOpenHeaderMenu(false);
      setShowRejectModal(false);
    }, [requisitionId]);

    // Fetch approval data when requisition is pending_approval
    useEffect(() => {
      if (requisition?.status === 'pending_approval' && requisition?.id) {
        getApprovalData({ id: requisition.id, type: 'requisition' });
      }
    }, [requisition?.status, requisition?.id, getApprovalData]);

    // Handle URL action parameter
    useEffect(() => {
      if (!autoTriggeredAction && urlAction === 'approved') {
        setShouldOpenHeaderMenu(true);
      }
      if (
        !urlAction ||
        !requisition ||
        requisition.status !== 'pending_approval' ||
        !isCurrentUserApprover ||
        processedActionId === `${requisitionId}-${urlAction}`
      ) {
        return;
      }

      const actionKey = `${requisitionId}-${urlAction}`;
      setProcessedActionId(actionKey);
      setUrlState({ action: undefined });

      if (urlAction === 'approved') {
        handleApproveAction(true);
      } else if (urlAction === 'rejected') {
        setShowRejectModal(true);
      }
    }, [
      urlAction,
      requisition,
      isCurrentUserApprover,
      processedActionId,
      requisitionId,
      setUrlState,
      handleApproveAction,
      autoTriggeredAction,
    ]);

    const handleEditClick = () => {
      if (requisition?.id) {
        setUrlState({ edit: requisition.id.toString(), view: undefined });
      }
    };

    const handleApprove = () => handleApproveAction(false);

    const handleReject = () => setShowRejectModal(true);

    const handleRejectSubmit = async (values: { note?: string } = {}) => {
      const isAutoTriggered = urlAction === 'rejected';
      await handleRejectAction(values.note, isAutoTriggered);
    };

    const handleRejectModalClose = () => {
      setShowRejectModal(false);
      if (urlAction === 'rejected') {
        setUrlState({ action: undefined });
        setProcessedActionId(null);
      }
    };

    const handleStatusChange = () => {
      queryClient.invalidateQueries({
        queryKey: [API_KEYS.getRequisition, requisitionId.toString()],
      });
    };

    const renderHeaderContent = (): React.ReactNode => {
      if (isRequisitionLoading && !requisition) {
        return null;
      }

      const MobileCloseButton = () => (
        <Center
          w="48px"
          h="48px"
          left="12px"
          top="5px"
          pos="absolute"
          bg="#fff"
          borderRadius="full"
          cursor="pointer"
          onClick={onClose}
          zIndex={1}
        >
          <Icon as={XIcon} w="20px" h="20px" />
        </Center>
      );

      if (isMobile) {
        return (
          <Flex
            position="relative"
            h="56px"
            align="center"
            px="16px"
            borderBottom="1px solid"
            borderColor="border-color"
            bg="#fff"
          >
            <MobileCloseButton />

            <Flex ml="56px" align="center" gap="8px" flexWrap="nowrap">
              <KText size="md-bold" color="secondary-900" whiteSpace="nowrap">
                Requisition approval
              </KText>

              {isPartOfGroup && requisition && (
                <PartOfGroupTag
                  requisition={requisition}
                  allRequisitions={allGroupRequisitions}
                />
              )}
            </Flex>
          </Flex>
        );
      }

      return (
        <RequisitionHeader
          requisition={requisition}
          isEditMode={false}
          readOnlyMode={readOnlyMode}
          isReqAssociated={isReqAssociated}
          canEdit={canEdit}
          onEditClick={handleEditClick}
          onStatusChange={handleStatusChange}
          onApprovalChange={handleApprovalChange}
          onClose={onClose}
          queryClient={queryClient}
          requisitionId={requisitionId.toString()}
          openMenuByDefault={shouldOpenHeaderMenu}
          isPartOfGroup={isPartOfGroup}
          availableRequisitions={availableRequisitions}
          allGroupRequisitions={allGroupRequisitions}
          isGrouped={readOnlyMode ? undefined : isGrouped}
        />
      );
    };

    const renderFooter = (): React.ReactNode => {
      if (isRequisitionLoading && !requisition) return null;
      if (urlAction === 'approved' || urlAction === 'rejected') {
        return null;
      }

      const shouldShowFooter =
        requisition?.status === 'pending_approval' && isCurrentUserApprover;

      if (!shouldShowFooter) return null;

      if (isMobile) {
        return (
          <Flex justify="space-between" w="100%" gap="12px" p="16px 10px">
            <Button
              flex="1"
              variant="mobile-danger-outline"
              onClick={handleReject}
              isLoading={isApproveActionLoading}
            >
              Reject
            </Button>
            <Button
              flex="1"
              variant="mobile-primary-lg"
              onClick={handleApprove}
              isLoading={isApproveActionLoading}
            >
              Approve
            </Button>
          </Flex>
        );
      }

      return (
        <Flex
          p="16px 24px"
          borderTop="1px solid"
          borderColor="border-color"
          justifyContent="flex-end"
          gap="6px"
        >
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleReject}
            isLoading={isApproveActionLoading}
          >
            Reject
          </Button>
          <Button
            variant="primary"
            onClick={handleApprove}
            isLoading={isApproveActionLoading}
          >
            Approve
          </Button>
        </Flex>
      );
    };

    const renderBodyContent = (): React.ReactNode => {
      if (
        (isRequisitionLoading && !requisition) ||
        isRequisitionsFormFieldsLoading
      ) {
        return <CenterSpinner />;
      }

      if (!requisition) {
        return (
          <Flex
            h="100%"
            w="100%"
            p="2rem"
            justifyContent="center"
            gap="12px"
            flexDir="column"
            alignItems="center"
          >
            <KText size="sm-medium" color="secondary-900">
              You don't have access to this requisition
            </KText>
            <Link to="/requisitions">
              <KText color="brand.100">Go to Requisitions list</KText>
            </Link>
          </Flex>
        );
      }

      return (
        <RequisitionDetailsBody
          requisition={requisition}
          customFieldsData={requisitionsFormFieldsData?.ats_custom_fields}
          dateFormat={dateFormat.label}
          readOnlyMode={readOnlyMode}
          formFields={requisitionsFormFieldsData}
          isReqAssociated={isReqAssociated}
          canEdit={canEdit}
          isPartOfGroup={isPartOfGroup}
          groupRequisitions={allGroupRequisitions}
          currentRequisitionId={requisition?.id}
        />
      );
    };

    const shouldShowFooter =
      requisition?.status === 'pending_approval' && isCurrentUserApprover;

    return (
      <>
        <KDrawer
          isOpen={isOpen}
          onClose={onClose}
          placement="right"
          size="lg"
          variant="wide"
          header={renderHeaderContent()}
          showCloseButton={!readOnlyMode && !isMobile}
          skipFooter={!shouldShowFooter}
          footer={renderFooter()}
          contentProps={
            isMobile
              ? {
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,
                }
              : undefined
          }
        >
          <SliderButtons
            onClose={onClose}
            onNext={onNext}
            onPrev={onPrev}
            applications={requisitions.map((req) => ({ appId: req.reqId }))}
            application={{ appId: requisitionId }}
            showNavItems={requisitions.length > 1}
          />
          {renderBodyContent()}
        </KDrawer>

        {showRejectModal && (
          <ModalWithForm
            isOpen={showRejectModal}
            onClose={handleRejectModalClose}
            headerText="Reason for rejection?"
            subHeaderText="Provide detailed feedback on why you are rejecting this requisition"
            primaryCtaText="Reject"
            formConfig={REJECT_FORM_FIELDS}
            handleSubmit={handleRejectSubmit}
            isCtaLoading={isApproveActionLoading}
            autoFocus={false}
            formContainerStyles={{
              m: '-10px 0',
            }}
            skipDirtyCheck={true}
          />
        )}
      </>
    );
  };
